"use client";

import { useEffect, useMemo, useState, lazy, Suspense } from "react";
import { useParams } from "next/navigation";
import Link from "next/link";
import "./Blogs.css";
import { MdKeyboardArrowRight } from "react-icons/md";
import Blogimageplaceholder from "@/assets/blogplaceholder.webp";
import { FaCalendarAlt, FaFacebookF, FaTwitter, FaLinkedinIn, FaShareAlt } from "react-icons/fa";
import { IoLogoWhatsapp, IoMdMail } from "react-icons/io";
import { BiSearch, BiX } from "react-icons/bi";
import { BsArrowRight } from "react-icons/bs";
import TableOfContents from "./BlogDetailComponents/TableOfContents";
import ReadingProgress from "./BlogDetailComponents/ReadingProgress";
import processBlogContent from "./BlogDetailComponents/processBlogContent";
import MobileShareButton from "./BlogDetailComponents/MobileShareButton";
import { useCourses } from "@/Api/hooks/HomePageApi/NavbarApi/useCourses";
import { useAllBlogs } from "@/Api/hooks/HomePageApi/TrendingBlogApi/useAllBlogs";
import { getBlogBySlug } from "./HomePage/TrendingBlogSection/services/blogsService";
import { slugifyCourseText } from "./CoursePage/courseRouteUtils";
import { getBlogPath } from "@/lib/blogUrl";
import { buildShareLinks, buildEmailShare, buildEmailHtml, copyEmailCard, extractFirstParagraph, getBlogShareUrl, getBlogSocialImageUrl, shareBlogWithImage } from "@/lib/blogShare";
import useShareImageFile from "./BlogDetailComponents/useShareImageFile";
import BlogEngagement from "./BlogDetailComponents/BlogEngagement";
import { linkifyFaqCourseMentions } from "@/lib/blogFaqLinks";
import { blogAssetUrl } from "@/lib/blogApi";

// Below-the-fold — related posts, share-preview card, and the inquiry form
// all render after the main article; no reason to ship their code upfront.
const MoreBlogs = lazy(() => import("./MoreBlogs"));
const BlogInquiryForm = lazy(() => import("./BlogInquiryForm"));

// Stable reference: `data: allCourses = []`-style destructuring defaults
// create a brand-new array every render while the query is still loading,
// which re-triggers any effect keyed on that value on every render —
// an infinite setState loop ("Maximum update depth exceeded"). A single
// module-level constant keeps the fallback reference stable across renders.
const EMPTY_ARRAY = [];

// FAQ course links: see src/lib/blogFaqLinks.js (answers only, never questions).
const courseHref = course => `/courses/${slugifyCourseText(course.courseCategory)}/${slugifyCourseText(course.courseName)}`;

const IMAGE_URL_RE = /(https?:\/\/\S+\.(?:png|jpg|jpeg|gif|webp))/gi;

// Turns bare (unlinked) image URLs sitting in the article's text into real
// <img> elements, and fixes up an <a> whose visible text is itself a single
// image URL. Two variants of that showed up in real blog content (confirmed
// against the live /blog API - every affected blog matches one of these, none
// use a genuine <img> tag for inline/CTA images):
//   - href === text: the editor auto-linked a pasted image URL to itself,
//     with no real destination - unwrap the whole <a> into a plain <img>.
//   - href !== text: a CTA banner - the author linked real, useful text
//     (e.g. a course URL or a bit.ly redirect) but typed the raw image URL
//     as the link's visible label instead of inserting the image, expecting
//     it to render as a clickable banner image. Keep the <a> (and its
//     destination) so the CTA still links out; replace only its text with
//     the <img>, so it becomes <a href="...cta..."><img src="...banner"></a>.
// Walks real DOM text nodes/attributes instead of regex-replacing the
// serialized innerHTML string — a plain string replace matches inside
// href="..." attribute values just as readily as visible text, splicing
// literal "<img .../>" markup into the middle of an <a> tag and corrupting
// it on reparse, which is what made a pasted image URL show up as raw URL
// text instead of an image whenever the editor had auto-linked it. An
// anchor whose visible text is not a single image URL is left untouched.
const convertImageUrlsToImages = (doc) => {
  Array.from(doc.querySelectorAll("a[href]")).forEach(anchor => {
    const href = anchor.getAttribute("href") || "";
    const text = anchor.textContent.trim();
    IMAGE_URL_RE.lastIndex = 0;
    const match = IMAGE_URL_RE.exec(text);
    if (!match || match[0] !== text) return;

    const img = doc.createElement("img");
    img.setAttribute("src", match[0]);
    img.setAttribute("alt", "Blog Image");
    img.setAttribute("loading", "lazy");
    img.setAttribute("class", "blog-content-image");

    if (href === text) {
      anchor.replaceWith(img);
    } else {
      anchor.replaceChildren(img);
    }
  });

  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  let node;
  while ((node = walker.nextNode())) {
    if (node.parentElement?.closest("a, script, style")) continue;
    IMAGE_URL_RE.lastIndex = 0;
    if (IMAGE_URL_RE.test(node.nodeValue)) textNodes.push(node);
  }
  textNodes.forEach(textNode => {
    const text = textNode.nodeValue;
    const frag = doc.createDocumentFragment();
    let lastIndex = 0;
    let match;
    IMAGE_URL_RE.lastIndex = 0;
    while ((match = IMAGE_URL_RE.exec(text))) {
      if (match.index > lastIndex) frag.appendChild(doc.createTextNode(text.slice(lastIndex, match.index)));
      const img = doc.createElement("img");
      img.setAttribute("src", match[1]);
      img.setAttribute("alt", "Blog Image");
      img.setAttribute("loading", "lazy");
      img.setAttribute("class", "blog-content-image");
      frag.appendChild(img);
      lastIndex = match.index + match[1].length;
    }
    if (lastIndex < text.length) frag.appendChild(doc.createTextNode(text.slice(lastIndex)));
    textNode.parentNode.replaceChild(frag, textNode);
  });
};

// Heading list for the table of contents, derived with a regex so it also works during SSR /
// the first client render (DOMParser is browser-only). The ids/text match what the DOM-based
// effect below produces (same slug rule); seeding it up front means the TOC is already in the
// server HTML instead of popping in above the article after hydration (CLS ~0.4).
const decodeEntities = (t) =>
  t
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)));
const extractHeadingsFromHtml = (html) => {
  if (!html) return [];
  const out = [];
  const re = /<h([1-6])\b[^>]*>([\s\S]*?)<\/h\1>/gi;
  let m;
  while ((m = re.exec(html))) {
    const text = decodeEntities(m[2].replace(/<[^>]*>/g, "")).trim();
    const id = text.toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "");
    out.push({ id, text });
  }
  return out;
};

// `initialBlog` is the same blog object app/(public)/blogs/[category_name]/
// [title]/page.js's server-side fetchBlogForMetadata() already fetches for
// generateMetadata()/JSON-LD. Seeding this component's state with it (instead
// of leaving selectedBlog as `useState(null)` populated only by the effect
// below) is what makes the server-rendered HTML contain the real <h1>/article
// body — without it, this whole page rendered nothing but an empty
// ".blog-details-skeleton-card" in the initial HTML (loading=true, no text
// content at all) until client JS ran, the same class of bug Task 2 fixed
// on the course-details page's CourseBanner/CourseCurriculum/FAQSection.
const BlogDetails = ({ initialBlog = null } = {}) => {
  // Next.js's useParams() (unlike react-router's) does not auto-decode
  // percent-escapes in dynamic segments — decode explicitly.
  const rawParams = useParams();
  const category_name = decodeURIComponent(rawParams.category_name || "");
  const rawTitle = decodeURIComponent(rawParams.title || "");
  const lastPart = rawTitle.split("-").pop();
  const id = /^\d+$/.test(lastPart) ? lastPart : null;

  const [selectedBlog, setSelectedBlog] = useState(initialBlog);
  const [headings, setHeadings] = useState(() => extractHeadingsFromHtml(initialBlog?.description));
  const [processedHtml, setProcessedHtml] = useState("");
  const [loading, setLoading] = useState(!initialBlog);
  const [searchQuery, setSearchQuery] = useState("");

  // FAQ Links only read courseName + courseCategory, so the light names-and-categories list (~17 KB,
  // the navbar's own endpoint, same 121 pairs as /courses/all) is used instead of /courses/all (~1.4 MB).
  const { data: allCourses = EMPTY_ARRAY } = useCourses();
  // "Recent Post" sidebar + Previous/Next + Related Blogs all derive from
  // this one already-fetched, newest-first list — no separate endpoints.
  const { data: blogs = EMPTY_ARRAY, isLoading: recentLoading } = useAllBlogs();

  // Sync selectedBlog from the server-fetched initialBlog. Keyed on
  // initialBlog itself (not just mount) so a client-side navigation to a
  // *different* blog — which re-runs page.js on the server and hands this
  // component a fresh initialBlog for the new route — still updates
  // selectedBlog correctly, with zero extra network requests: the server
  // already fetched this exact blog for this exact request. A client fetch
  // only happens as a fallback when the server genuinely returned nothing
  // (e.g. a transient backend failure), not as a routine "fetch it again"
  // on every mount — that used to fire a duplicate request straight to the
  // backend even when initialBlog was already correct and fresh.
  useEffect(() => {
    if (initialBlog) {
      // Syncs state from the server-provided blog prop (an external source).
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedBlog(initialBlog);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const fetchSelectedBlog = async () => {
      setLoading(true);
      try {
        const blog = await getBlogBySlug(rawTitle);
        if (!cancelled) setSelectedBlog(blog);
      } catch (error) {
        console.error("Error fetching selected blog:", error);
        if (!cancelled) setSelectedBlog(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSelectedBlog();
    return () => {
      cancelled = true;
    };
  }, [initialBlog, rawTitle]);

  const filteredBlogs = searchQuery.trim()
    ? blogs.filter(blog => blog.title.toLowerCase().includes(searchQuery.toLowerCase()) || blog.category_name.toLowerCase().includes(searchQuery.toLowerCase()))
    : blogs;

  // Parse HTML for headings and inline images
  useEffect(() => {
    if (selectedBlog?.description) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(selectedBlog.description, "text/html");
      const foundHeadings = [];
      const headingTags = doc.querySelectorAll("h1, h2, h3, h4, h5, h6");
      headingTags.forEach(heading => {
        const text = heading.textContent.trim();
        const headingId = text.toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "");
        heading.setAttribute("id", headingId);
        foundHeadings.push({ id: headingId, text });
      });
      convertImageUrlsToImages(doc);
      // Per-blog opt-out: autoLinkCourses === false (null/missing = on).
      if (selectedBlog.autoLinkCourses !== false) linkifyFaqCourseMentions(doc, allCourses, courseHref);
      // Syncs from the fetched blog's HTML content (an external source).
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHeadings(foundHeadings);
      setProcessedHtml(doc.body.innerHTML);
    }
  }, [selectedBlog, allCourses]);

  // processBlogContent re-parses the whole article (DOMParser + regex); run once per processedHtml instead of
  // on every render (search typing, share/engagement state, list loads all re-render this component).
  const articleHtml = useMemo(() => (processedHtml ? processBlogContent(processedHtml) : ""), [processedHtml]);

  const [emailNotice, setEmailNotice] = useState("");
  // Set only when this browser can attach the blog's image file to a share.
  const shareImageFile = useShareImageFile(selectedBlog);

  const handleImageError = e => {
    e.target.src = Blogimageplaceholder.src;
  };

  // Canonical share URL/title/links come from src/lib/blogShare.js (shared
  // with the mobile share sheet) - derived from the blog itself, not from
  // window.location, so tracking params never leak into shared links. The
  // email body leads with the blog's real first paragraph.
  const blogShareTitle = selectedBlog?.title || "Hachion Blog";
  const blogUrl = getBlogShareUrl(selectedBlog, `/blogs/${category_name}/${rawTitle}`);
  const emailParts = {
    url: blogUrl,
    title: blogShareTitle,
    html: selectedBlog?.description,
    metaDescription: selectedBlog?.meta_description,
    imageUrl: getBlogSocialImageUrl(selectedBlog),
  };
  const shareHrefs = buildShareLinks(emailParts);
  const openShare = (href, features = "noopener,noreferrer,width=600,height=500") => () => {
    const win = window.open(href, "_blank", features);
    if (win) win.opener = null;
  };
  // Native share sheet with the real image attached (not a link preview).
  const shareWithImage = async () => {
    try {
      const description = selectedBlog?.meta_description || extractFirstParagraph(selectedBlog?.description, { maxLength: 200 });
      const result = await shareBlogWithImage({ file: shareImageFile, title: blogShareTitle, description, url: blogUrl });
      if (result === "shared") setEmailNotice("");
    } catch {
      setEmailNotice("Could not open the share sheet. Use the share icons instead.");
    }
  };
  const shareLinks = {
    facebook: openShare(shareHrefs.facebook),
    twitter: openShare(shareHrefs.twitter),
    linkedin: openShare(shareHrefs.linkedin, "noopener,noreferrer,width=600,height=600"),
    whatsapp: openShare(shareHrefs.whatsapp, "noopener,noreferrer"),
    // Gmail's compose URL carries plain text only, so alongside it the image
    // card (clickable image + first paragraph + link) is copied for pasting into
    // the body. The copy starts first, synchronously within the click, so it
    // keeps the user gesture; the compose window opens right after.
    email: () => {
      const copying = copyEmailCard({ html: buildEmailHtml(emailParts), text: buildEmailShare(emailParts).body });
      openShare(shareHrefs.email, "noopener,noreferrer")();
      copying.then((ok) =>
        setEmailNotice(ok ? "Blog card with image copied - press Ctrl+V in the email to insert it." : "The email includes the blog link; the image card could not be copied.")
      );
    },
  };

  // Shared slug logic (src/lib/blogUrl.js) — same one every other blog link
  // on this page and site-wide uses, so every link points at the same
  // canonical URL for a given blog.
  const getBlogUrl = (blog) => getBlogPath(blog) || "/blogs";

  // Related Blogs: same category first, filled from latest if fewer than
  // 3, current post always excluded. Derived from the same already-fetched
  // `blogs` list — no new API call, no hardcoded blog names.
  const relatedBlogs = useMemo(() => {
    if (!selectedBlog) return [];
    const others = blogs.filter((b) => b.id !== selectedBlog.id);
    const sameCategory = others.filter((b) => b.category_name === selectedBlog.category_name);
    const filler = others.filter((b) => b.category_name !== selectedBlog.category_name);
    return [...sameCategory, ...filler].slice(0, 6);
  }, [selectedBlog, blogs]);

  return (
    <div className="home-background">
      <ReadingProgress />
      <MobileShareButton selectedBlog={selectedBlog} />

      <div className="blogs-header">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link href="/">Home</Link> <MdKeyboardArrowRight />
            </li>
            <li className="breadcrumb-item">
              <Link href="/blogs">Blog</Link> <MdKeyboardArrowRight />
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              {category_name?.replace(/-/g, " ") || "Loading..."}
            </li>
          </ol>
        </nav>
      </div>

      <div className="detail-blog container">
        <div className="detail-blog-right">
          {loading ? (
            <div className="blog-details-skeleton-card large"></div>
          ) : selectedBlog ? (
            <>
              <div className="detail-middle">
                {/* Above-the-fold hero/LCP image — must load eagerly. */}
                {(() => {
                  const heroSrc = blogAssetUrl(selectedBlog.blog_image);
                  // Uploaded blog images can be far larger than a phone needs (the LCP
                  // image was ~90 KB). Route https images through Next's image
                  // optimizer (api host is already in images.remotePatterns) for
                  // right-sized WebP; the original URL stays as src/fallback.
                  const heroSrcSet = heroSrc && heroSrc.startsWith("https://")
                    ? [384, 640, 828, 1080].map((w) => `/_next/image?url=${encodeURIComponent(heroSrc)}&w=${w}&q=75 ${w}w`).join(", ")
                    : undefined;
                  return (
                    <img
                      src={heroSrc || Blogimageplaceholder.src}
                      srcSet={heroSrcSet}
                      sizes="(max-width: 768px) 100vw, 800px"
                      alt={selectedBlog.title}
                      onError={(e) => { e.currentTarget.removeAttribute("srcset"); handleImageError(e); }}
                      fetchPriority="high"
                      className="featured-blog-image"
                    />
                  );
                })()}
                <div>
                  <div className="detail-top">
                    <div className="detail-top-date" style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
                      {(() => {
                        const d = new Date(selectedBlog.date);
                        return d.toLocaleDateString("en-US", { day: "2-digit", month: "long", year: "numeric", timeZone: "UTC" });
                      })()}
                      <BlogEngagement key={selectedBlog.id} blogId={selectedBlog.id} initialViews={selectedBlog.viewCount} part="views" />
                    </div>
                  </div>
                  <h1 className="blog-detail-title">{selectedBlog.title}</h1>
                </div>
              </div>

              {/* The figures come from processedHtml, which needs DOMParser and so only exists after the client
                  effect has run. Until then an invisible chip of the same size is rendered, so the chip appearing
                  doesn't push the table of contents and article down (this was the page's layout shift). */}
              {selectedBlog.description && (
                processedHtml ? (
                  <div className="estimated-read-time">
                    ⏱️ {Math.ceil(processedHtml.split(' ').length / 200)} min read
                    <span className="read-time-separator">•</span>
                    <span className="word-count">{processedHtml.split(' ').length} words</span>
                  </div>
                ) : (
                  <div className="estimated-read-time" aria-hidden="true" style={{ visibility: "hidden" }}>
                    ⏱️ 10 min read
                    <span className="read-time-separator">•</span>
                    <span className="word-count">10000 words</span>
                  </div>
                )
              )}

              {headings.length > 0 && <TableOfContents headings={headings} />}

              {/* processedHtml (heading ids, image-tag rewrites, FAQ
                  auto-links) and processBlogContent (Q&A card wrapping) both
                  require DOMParser, a browser-only API — unavailable during
                  SSR, so they're only computed by the client-side effect
                  above. Until that runs, fall back to the raw
                  (server-fetched, unprocessed) article HTML so the actual
                  article text — not an empty shell — is what's present in
                  the server-rendered HTML a crawler sees. */}
              <div
                className="topics enhanced-blog-content"
                dangerouslySetInnerHTML={{ __html: articleHtml || selectedBlog.description || "" }}
              />
            </>
          ) : (
            <div className="blog-not-found">
              <p>Blog post not found</p>
            </div>
          )}

          <div className="detail-right">
            <div className="detail-right-icon">
              <p className="share-label">Share :</p>
              <button type="button" className="share-btn" onClick={shareLinks.facebook} aria-label="Share on Facebook"><FaFacebookF className="social-icon facebook" aria-hidden="true" /></button>
              <button type="button" className="share-btn" onClick={shareLinks.twitter} aria-label="Share on X (Twitter)"><FaTwitter className="social-icon twitter" aria-hidden="true" /></button>
              <button type="button" className="share-btn" onClick={shareLinks.linkedin} aria-label="Share on LinkedIn"><FaLinkedinIn className="social-icon linkedin" aria-hidden="true" /></button>
              <button type="button" className="share-btn" onClick={shareLinks.whatsapp} aria-label="Share on WhatsApp"><IoLogoWhatsapp className="social-icon whatsapp" aria-hidden="true" /></button>
              <button type="button" className="share-btn" onClick={shareLinks.email} aria-label="Share by email"><IoMdMail className="social-icon mail" aria-hidden="true" /></button>
              {shareImageFile && (
                <button type="button" className="share-btn" onClick={shareWithImage} aria-label="Share with image" title="Share with image"><FaShareAlt className="social-icon" aria-hidden="true" /></button>
              )}
              {/* Bookmark sits immediately after the share buttons, in the same row; the two actions are independent. */}
              <BlogEngagement key={`bookmark-${selectedBlog?.id}`} blogId={selectedBlog?.id} part="bookmark" />
              {emailNotice && <span role="status" aria-live="polite" className="share-email-notice" style={{ fontSize: "0.8rem" }}>{emailNotice}</span>}
            </div>
          </div>
        </div>

        <div className="detail-blog-left">
          <div className="search-input-blog">
            <div className="search-input-wrapper-blog">
              <BiSearch className="search-icon" />
              <input type="text" placeholder="Search blogs by title or category..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
              {searchQuery && (
                <button className="search-clear-btn" onClick={() => setSearchQuery("")} aria-label="Clear search">
                  <BiX />
                  <span className="clear-tooltip">Clear</span>
                </button>
              )}
            </div>

            {searchQuery.trim() && (
              <div className="search-dropdown">
                <div className="dropdown-header">
                  <span className="results-count">{filteredBlogs.length} result{filteredBlogs.length !== 1 ? 's' : ''}</span>
                  {filteredBlogs.length > 6 && (
                    <span className="showing-count">Showing {Math.min(filteredBlogs.length, 10)} of {filteredBlogs.length}</span>
                  )}
                </div>

                <div className="dropdown-results">
                  {filteredBlogs.length > 0 ? filteredBlogs.slice(0, 10).map((blog) => (
                    <Link key={blog.id} href={getBlogUrl(blog)} className="search-dropdown-item" onClick={() => { setSearchQuery(""); window.scrollTo(0, 0); }}>
                      <div className="image-wrapper">
                        <img src={blog.blog_image} alt={blog.title} className="search-dropdown-img" onError={e => e.target.src = Blogimageplaceholder.src} />
                      </div>
                      <div className="search-dropdown-text">
                        <span className="search-dropdown-category">{blog.category_name}</span>
                        <p className="search-dropdown-title">{blog.title}</p>
                        <span className="read-more-hint">
                          Read <BsArrowRight className="arrow-icon" />
                        </span>
                      </div>
                    </Link>
                  )) : (
                    <div className="search-no-results">
                      <BiSearch className="no-results-icon" />
                      <div className="no-results-text">
                        No blogs found for &quot;<strong>{searchQuery}</strong>&quot;
                      </div>
                      <div className="no-results-suggestion">Try different keywords or browse all blogs</div>
                    </div>
                  )}
                </div>

                {filteredBlogs.length > 5 && <div className="scroll-indicator" />}
              </div>
            )}
          </div>

          <h3>Recent Post</h3>

          {recentLoading ? Array.from({ length: 5 }).map((_, i) => (
            // Laid out like the loaded .recent-post-item (80px thumbnail beside two text lines, 20px gap below =
            // 100px per row). The stacked layout the CSS gives this skeleton is 138px per row, so when the list
            // loaded it shrank by ~190px and pulled everything below it (the enquiry form) up.
            <div className="recent-post-skeleton" key={i} style={{ flexDirection: "row", alignItems: "flex-start", gap: 10, marginBottom: 20 }}>
              <div className="recent-skeleton-image" style={{ flexShrink: 0 }}></div>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8, paddingTop: 4 }}>
                <div className="recent-skeleton-text subtitle"></div>
                <div className="recent-skeleton-text title"></div>
              </div>
            </div>
          )) : blogs.length > 0 ? blogs.slice(0, 5).map(blog => {
            const recentPostUrl = getBlogUrl(blog);
            return (
              <Link key={blog.id} href={recentPostUrl} className="recent-post-item" style={{ textDecoration: "none", color: "inherit" }} onClick={() => window.scrollTo(0, 0)}>
                <img src={blog.blog_image} alt={blog.title} className="recent-post-img" style={{ flexShrink: 0 }} onError={e => e.target.src = Blogimageplaceholder.src} loading="lazy" />
                <div className="recent-post-text">
                  <div className="recent-post-row">
                    <FaCalendarAlt className="recent-post-date-icon" />
                    <p className="recent-post-date">
                      {(() => {
                        const d = new Date(blog.date);
                        return d.toLocaleDateString("en-US", { day: "2-digit", month: "long", year: "numeric", timeZone: "UTC" });
                      })()}
                    </p>
                  </div>
                  <h5 className="recent-post-title">{blog.title}</h5>
                </div>
              </Link>
            );
          }) : <p className="no-blogs-message">📭 No blogs available</p>}

          {selectedBlog && !loading && (
            <Suspense fallback={null}>
              <BlogInquiryForm blogTitle={selectedBlog?.category_name} />
            </Suspense>
          )}
        </div>
      </div>

      <div className="blog-bottom">
        <Suspense fallback={null}>
          <MoreBlogs scrollToTop={false} blogs={relatedBlogs} loading={recentLoading} />
        </Suspense>
      </div>
    </div>
  );
};
export default BlogDetails;
